import React, { useEffect, useCallback, memo, useState } from 'react';
import { useLocation } from 'react-router';
import { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import JoyrideWrapper from './JoyrideWrapper';
import { useOnboardingStore } from '../../zustand/onboardingStore';
import { getTourSteps } from './tours';
import { TourType, JoyrideCallbackData } from '../../types/onboarding';
import { useUser } from '../../utils/hooks/user';
import { tourRoutes } from './tourRoutes';

// Map routes to tour types
const routeToTourMap: Record<string, TourType> = {
  '/': TourType.OVERVIEW,
  '/conversations': TourType.CONVERSATIONS,
  '/agents': TourType.AGENTS,
  '/settings': TourType.SETTINGS,
  '/reports': TourType.REPORTS,
  '/records': TourType.RECORDS,
  '/verification': TourType.DOCUMENTS,
  '/esignature': TourType.DOCUMENTS,
  '/call-requests': TourType.CALENDAR,
  '/scheduler': TourType.SCHEDULER,
  '/logs': TourType.LOGS,
  '/teams': TourType.TEAMS
};

const OnboardingContainer: React.FC = () => {
  const location = useLocation();
  const user = useUser();
  const [readyTour, setReadyTour] = useState<TourType | null>(null);

  const {
    run,
    stepIndex,
    tourType,
    isOnboardingEnabled,
    completedTours,
    skippedTours,
    userPreferences,
    startTour,
    stopTour,
    completeTour,
    skipTour,
    setStepIndex,
    syncTourProgressFromBackend
  } = useOnboardingStore();

  // Sync tour progress from backend on app load
  useEffect(() => {
    if (!user) return;

    // Load tour progress from backend to sync across devices/sessions
    syncTourProgressFromBackend();
  }, [user, syncTourProgressFromBackend]);

  // Check if user is new (for first login tour)
  useEffect(() => {
    if (!user) return;

    const isNewUser =
      completedTours.length === 0 &&
      skippedTours.length === 0 &&
      userPreferences.showOnLogin &&
      isOnboardingEnabled;

    if (isNewUser) {
      // Delay to ensure DOM is ready
      const timer = setTimeout(() => {
        const progress = useOnboardingStore.getState();
        if (
          !progress.run &&
          progress.completedTours.length === 0 &&
          progress.skippedTours.length === 0
        ) {
          startTour(TourType.FIRST_LOGIN);
        }
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [user, completedTours, skippedTours, userPreferences.showOnLogin, isOnboardingEnabled, startTour]);

  // Auto-start tour when visiting a new section
  useEffect(() => {
    if (!isOnboardingEnabled || !userPreferences.autoStartTours || run) return;

    const currentPath = location.pathname;
    const currentTourType = routeToTourMap[currentPath];

    if (
      currentTourType &&
      !completedTours.includes(currentTourType) &&
      !skippedTours.includes(currentTourType) &&
      currentTourType !== TourType.FIRST_LOGIN
    ) {
      // Delay to ensure DOM elements are rendered
      const timer = setTimeout(() => {
        const progress = useOnboardingStore.getState();
        if (
          progress.run ||
          progress.completedTours.includes(currentTourType) ||
          progress.skippedTours.includes(currentTourType)
        ) return;
        // Check if first element of tour exists before starting
        const tourSteps = getTourSteps(currentTourType);
        if (tourSteps.length > 0 && tourSteps[0].target) {
          const firstTarget = document.querySelector(tourSteps[0].target as string);
          if (firstTarget) {
            startTour(currentTourType);
          } else {
            console.warn(`Tour target not found: ${tourSteps[0].target}`);
          }
        }
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [location.pathname, isOnboardingEnabled, userPreferences.autoStartTours, run, completedTours, skippedTours, startTour]);

  // Route components can mount after navigation (and some are lazy loaded).
  // Mount Joyride only once the selected page and its first target are present.
  useEffect(() => {
    setReadyTour(null);
    if (!run || !tourType || location.pathname !== tourRoutes[tourType]) return;

    const firstTarget = getTourSteps(tourType)[0]?.target;
    if (!firstTarget) return;

    let attempts = 0;
    let timer: ReturnType<typeof setTimeout>;
    const waitForTarget = () => {
      if (typeof firstTarget !== 'string' || document.querySelector(firstTarget)) {
        setReadyTour(tourType);
      } else if (attempts++ < 50) {
        timer = setTimeout(waitForTarget, 100);
      } else {
        console.warn(`Tour target not found: ${firstTarget}`);
      }
    };
    waitForTarget();
    return () => clearTimeout(timer);
  }, [run, tourType, location.pathname]);

  // Handle Joyride callbacks
  const handleJoyrideCallback = useCallback(
    (data: JoyrideCallbackData) => {
      const { action, index, status, type } = data;

      if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
        // Update step index
        setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
      } else if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        // Tour finished or skipped
        if (tourType) {
          if (status === STATUS.FINISHED) {
            completeTour(tourType);
          } else if (status === STATUS.SKIPPED) {
            skipTour(tourType);
          }
        }
        stopTour();
      }
    },
    [tourType, setStepIndex, completeTour, skipTour, stopTour]
  );

  // Get current tour steps
  const currentSteps = tourType ? getTourSteps(tourType) : [];

  if (!isOnboardingEnabled || !run || !tourType || readyTour !== tourType || location.pathname !== tourRoutes[tourType]) {
    return null;
  }

  return (
    <JoyrideWrapper
      steps={currentSteps}
      run={run}
      stepIndex={stepIndex}
      continuous
      showProgress
      showSkipButton
      callback={handleJoyrideCallback}
    />
  );
};

export default memo(OnboardingContainer);
